/*
 * Description:
 * ---------------------
 * Agent Rules: for the person's organisation, whether every agent change is
 * frozen, the rules bundle in force (version, log or enforce mode and since
 * when, until when), what each box last reported holding of both, and the
 * rules' text behind one disclosure. Tenant admins freeze every agent change,
 * or unfreeze, with the confirmation in the page itself: the question takes
 * the button's place and names every box it stops. A freeze is signed by the
 * customer's authority key on the server and reaches each box with its next
 * heartbeat reply.
 *
 * Fed by /api/v1/mesh/freeze, /mesh/unfreeze and /mesh/rules (approvalApi.js,
 * server/pkg/approvals); a server that lists no rules still shows the freeze.
 * All wording is in approvalWords.js.
 *
 * ! file components/AgentRulesPanel.js
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import FeatureUnavailableNotice from "./FeatureUnavailableNotice";
import CustomerPick, { useCustomer } from "./CustomerPick";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { useSession } from "./PortalGate";
import { NotServed, ROUTES, ask } from "./approvalApi";
import * as W from "./approvalWords";
import "./Firewall.css";
import "./Mesh.css";
import "./Approvals.css";

const READ_MS = 15000;

const Status = ({ tone, children }) => (
  <span className={`mesh-answer is-${tone}`}><span className="mesh-dot" aria-hidden="true" /> {children}</span>
);

/* The freeze or unfreeze control, which asks in place before it acts. */
function FreezeControl({ name, boxes, frozen, act, busy }) {
  const [asking, setAsking] = useState(false);
  const [why, setWhy] = useState("");
  if (!asking) {
    return (
      <div className="ap-buttons">
        <button type="button" className={`fw-btn is-big${frozen ? "" : " is-danger-line"}`} onClick={() => setAsking(true)}>
          {frozen ? W.UNFREEZE : W.FREEZE}
        </button>
      </div>
    );
  }
  const done = () => { setAsking(false); setWhy(""); };
  return (
    <div className={`ar-confirm${frozen ? "" : " is-freeze"}`} role="group" aria-label={frozen ? W.UNFREEZE : W.FREEZE}>
      <p className="ar-ask">{frozen ? W.askUnfreeze(name) : W.askFreeze(name, boxes.length)}</p>
      {!frozen && boxes.length > 0 && <p className="ar-names">{boxes.map((b) => <span key={b.node} className="ap-box">{b.node}</span>)}</p>}
      {!frozen && (
        <label className="ap-field">
          <span>{W.FREEZE_WHY}</span>
          <input className="fw-input" value={why} maxLength={200} onChange={(e) => setWhy(e.target.value)} />
        </label>
      )}
      <div className="ap-buttons">
        <button type="button" className={`fw-btn is-big ${frozen ? "is-primary" : "is-danger"}`} disabled={busy || (!frozen && !why.trim())}
          onClick={() => act(frozen ? "unfreeze" : "freeze", why.trim()).then((ok) => ok && done())}>
          {frozen ? W.UNFREEZE_NOW : W.FREEZE_NOW}
        </button>
        <button type="button" className="fw-btn is-big" disabled={busy} onClick={done}>{frozen ? W.KEEP_FROZEN : W.NOT_NOW}</button>
      </div>
    </div>
  );
}

/* One organisation's rules and freeze, read again every 15 s. */
function Rules({ serverApiUrl, customer, name }) {
  const session = useSession();
  const [freezes, setFreezes] = useState(null);
  // The rules as the server lists them: undefined while it lists none.
  const [rules, setRules] = useState(undefined);
  const [error, setError] = useState("");
  const [notServed, setNotServed] = useState(false);
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const read = useCallback(async () => {
    try {
      const [f, r] = await Promise.all([
        ask(serverApiUrl, ROUTES.freezes(customer)),
        ask(serverApiUrl, ROUTES.rules(customer)).catch((e) => { if (e instanceof NotServed) return undefined; throw e; }),
      ]);
      if (!mounted.current) return;
      setFreezes(f);
      setRules(r);
      setError("");
    } catch (e) {
      if (!mounted.current) return;
      if (e instanceof NotServed) setNotServed(true);
      else setError(e.message);
    }
  }, [serverApiUrl, customer]);
  useVisibleInterval(read, READ_MS);

  /* act freezes or unfreezes, and answers whether it did. */
  const act = async (what, why) => {
    setBusy(true);
    try {
      const at = freezes.customer;
      await ask(serverApiUrl, what === "freeze" ? ROUTES.freeze : ROUTES.unfreeze, what === "freeze" ? { customer: at, why } : { customer: at });
      await read();
      return true;
    } catch (e) {
      if (mounted.current) setError(e instanceof NotServed ? W.NOT_ON_SERVER : e.message);
      return false;
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  if (notServed) return <FeatureUnavailableNotice compact nodeId="this Weavian server" feature="Agent rules" detail={W.NOT_ON_SERVER} />;
  const problem = error && <div className="fw-notice is-bad" role="alert"><FiAlertTriangle size={16} /> <span>{error}</span></div>;
  if (!freezes) return problem || <p className="fw-muted">Reading…</p>;
  const now = new Date();
  const shownName = name || freezes.customer;
  const frozen = freezes.in_force ? freezes.freezes[0] : null;
  const boxes = freezes.boxes || [];
  const bundle = rules && W.inForce(rules);
  const waiting = rules ? W.rulesWaiting(rules) : "";
  const fz = W.freezeSay(frozen, now);
  const bs = W.bundleSay(bundle, now);
  return (
    <>
      {problem}
      <section className={`fw-card ar-card${frozen ? " is-frozen" : ""}`}>
        <h3 className="fw-card-title">{shownName}</h3>
        <p className="ar-state"><Status tone={fz.tone}>{fz.text}</Status></p>
        {session.isTenantAdmin
          ? <FreezeControl name={shownName} boxes={boxes} frozen={!!frozen} act={act} busy={busy} />
          : <p className="fw-muted">{W.ADMINS_ONLY}</p>}
        <p className="ar-state"><Status tone={bs.tone}>{bs.text}</Status></p>
        {bundle?.mode === "log" && <p className="fw-muted">{W.LOG_THEN_ENFORCE}</p>}
        {waiting && <p className="ar-state"><Status tone="wait">{waiting}</Status></p>}
        {boxes.length > 0 && (
          <ul className="ap-boxes">
            {boxes.map((b) => {
              const h = W.boxRules(b, frozen, now);
              return (
                <li key={b.node}>
                  <span className="ap-box">{b.node}</span>
                  <Status tone={h.tone}>{h.text}</Status>
                </li>
              );
            })}
          </ul>
        )}
        {bundle && (
          <details className="mesh-how">
            <summary>{W.READ_RULES}</summary>
            <p className="fw-muted">{W.RULES_PRINT}</p>
            <code className="ap-raw">{bundle.digest}</code>
            {bundle.policies && <pre className="ap-text">{bundle.policies}</pre>}
          </details>
        )}
      </section>
    </>
  );
}

export default function AgentRulesPanel({ serverApiUrl }) {
  const pick = useCustomer(serverApiUrl);
  return (
    <div className="fw">
      {pick.domains?.length > 1 && <section className="fw-card"><CustomerPick {...pick} /></section>}
      {pick.domains === null ? <p className="fw-muted">Reading…</p>
        : pick.none ? <section className="fw-card"><p className="ap-said">{W.NO_CUSTOMERS}</p></section>
          : <Rules key={pick.customer} serverApiUrl={serverApiUrl} customer={pick.customer} name={pick.name} />}
    </div>
  );
}
